import React, { useState, useEffect } from 'react';
import './MonthReflection.css';
import StyledInput from './StyledInput';
import StyledTextarea from './StyledTextarea';
import { saveMonthData, loadMonthData } from '../utils/storage';

const MonthReflection = ({ monthIndex, monthName, year }) => {
  const [data, setData] = useState({
    presupuesto: {
      items: [
        { id: 'gastosViaje', nombre: 'Gastos de Viaje', real: '' },
        { id: 'alquiler', nombre: 'Alquiler', real: '' },
        { id: 'tarjetas', nombre: 'Tarjetas', real: '' },
        { id: 'super', nombre: 'Super', real: '' },
        { id: 'auto', nombre: 'Auto', real: '' }
      ]
    },
    tareas: [],
    empaque: {
      documentos: {
        pasaporte: false,
        visa: false,
        seguroViaje: false,
        reservas: false,
        tarjetasCredito: false,
        copiasDocumentos: false
      },
      ropaElectronica: {
        ropaAdecuada: false,
        calzadoComodo: false,
        cargadorMovil: false,
        adaptadorEnchufe: false,
        auriculares: false,
        camara: false,
        powerBank: false,
        medicamentos: false
      }
    },
    reflexion: ''
  });

  useEffect(() => {
    const savedData = loadMonthData(monthIndex, year);
    if (savedData && savedData.reflection) {
      // Migrar datos antiguos si existen
      const oldPresupuesto = savedData.reflection.presupuesto;
      let presupuestoItems = [];
      
      if (oldPresupuesto?.items) {
        // Migrar items antiguos: mantener solo el campo 'real'
        presupuestoItems = oldPresupuesto.items.map(item => ({
          id: item.id,
          nombre: item.nombre,
          real: item.real || ''
        }));
      } else {
        // Migrar formato antiguo
        const defaultItems = [
          { id: 'gastosViaje', nombre: 'Gastos de Viaje', real: '' },
          { id: 'alquiler', nombre: 'Alquiler', real: '' },
          { id: 'tarjetas', nombre: 'Tarjetas', real: '' },
          { id: 'super', nombre: 'Super', real: '' },
          { id: 'auto', nombre: 'Auto', real: '' }
        ];
        
        // Migrar datos antiguos si existen (solo mantener 'real')
        if (oldPresupuesto?.gastosViaje) {
          defaultItems[0] = { 
            ...defaultItems[0], 
            real: oldPresupuesto.gastosViaje.real || '' 
          };
        }
        if (oldPresupuesto?.ahorroAcumulado) {
          defaultItems.push({ 
            id: 'ahorroAcumulado', 
            nombre: 'Ahorro Acumulado', 
            real: oldPresupuesto.ahorroAcumulado.real || ''
          });
        }
        
        presupuestoItems = defaultItems;
      }
      
      // Migrar tareas antiguas si existen
      let tareas = [];
      if (savedData.reflection.tareas) {
        if (Array.isArray(savedData.reflection.tareas)) {
          tareas = savedData.reflection.tareas;
        } else {
          // Convertir formato antiguo (objeto) a array
          tareas = Object.entries(savedData.reflection.tareas)
            .filter(([_, checked]) => checked)
            .map(([nombre, _]) => ({ id: Date.now() + Math.random(), nombre, completada: false }));
        }
      }
      
      setData({
        presupuesto: {
          items: presupuestoItems
        },
        tareas: tareas,
        empaque: {
          documentos: {
            pasaporte: false,
            visa: false,
            seguroViaje: false,
            reservas: false,
            tarjetasCredito: false,
            copiasDocumentos: false,
            ...savedData.reflection.empaque?.documentos
          },
          ropaElectronica: {
            ropaAdecuada: false,
            calzadoComodo: false,
            cargadorMovil: false,
            adaptadorEnchufe: false,
            auriculares: false,
            camara: false,
            powerBank: false,
            medicamentos: false,
            ...savedData.reflection.empaque?.ropaElectronica
          }
        },
        reflexion: savedData.reflection.reflexion || ''
      });
    }
  }, [monthIndex, year]);

  const handleChange = (field, value) => {
    const newData = { ...data, [field]: value };
    setData(newData);
    const monthData = loadMonthData(monthIndex, year) || {};
    const updatedData = { ...monthData, reflection: newData };
    saveMonthData(monthIndex, updatedData, year);
  };

  const handlePresupuestoChange = (itemId, value) => {
    const newItems = data.presupuesto.items.map(item => {
      if (item.id === itemId) {
        return { ...item, real: value };
      }
      return item;
    });
    handleChange('presupuesto', { items: newItems });
  };

  const addPresupuestoItem = () => {
    const newItem = {
      id: Date.now() + Math.random(),
      nombre: '',
      real: ''
    };
    const newItems = [...data.presupuesto.items, newItem];
    handleChange('presupuesto', { items: newItems });
  };

  const removePresupuestoItem = (itemId) => {
    const newItems = data.presupuesto.items.filter(item => item.id !== itemId);
    handleChange('presupuesto', { items: newItems });
  };

  const handleTareaChange = (tareaId) => {
    const newTareas = data.tareas.map(tarea => 
      tarea.id === tareaId 
        ? { ...tarea, completada: !tarea.completada }
        : tarea
    );
    handleChange('tareas', newTareas);
  };

  const addTarea = () => {
    const newTarea = {
      id: Date.now() + Math.random(),
      nombre: '',
      completada: false
    };
    const newTareas = [...data.tareas, newTarea];
    handleChange('tareas', newTareas);
  };

  const removeTarea = (tareaId) => {
    const newTareas = data.tareas.filter(tarea => tarea.id !== tareaId);
    handleChange('tareas', newTareas);
  };

  const updateTareaNombre = (tareaId, nombre) => {
    const newTareas = data.tareas.map(tarea => 
      tarea.id === tareaId ? { ...tarea, nombre } : tarea
    );
    handleChange('tareas', newTareas);
  };

  const updatePresupuestoNombre = (itemId, nombre) => {
    const newItems = data.presupuesto.items.map(item => 
      item.id === itemId ? { ...item, nombre } : item
    );
    handleChange('presupuesto', { items: newItems });
  };

  const handleEmpaqueChange = (categoria, item) => {
    const newEmpaque = {
      ...data.empaque,
      [categoria]: {
        ...data.empaque[categoria],
        [item]: !data.empaque[categoria][item]
      }
    };
    handleChange('empaque', newEmpaque);
  };


  return (
    <div className="month-reflection">
      <header className="reflection-header">
        <h1 className="month-title">{monthName} {year} - Seguimiento</h1>
      </header>

      <section className="presupuesto-section">
        <div className="section-header">
          <div className="section-title-with-icon">
            <span className="section-icon floating">💰</span>
            <h2 className="section-title-text">Seguimiento de Presupuesto Mensual</h2>
          </div>
          <button 
            type="button" 
            className="add-item-btn pulse-on-hover"
            onClick={addPresupuestoItem}
          >
            <span className="btn-icon">➕</span>
            <span>Agregar Item</span>
          </button>
        </div>
        <table className="presupuesto-table">
          <thead>
            <tr>
              <th>Categoría</th>
              <th>Gasto Real</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.presupuesto.items.map((item) => (
              <tr key={item.id}>
                <td>
                  <input
                    type="text"
                    className="presupuesto-nombre-input"
                    value={item.nombre}
                    onChange={(e) => updatePresupuestoNombre(item.id, e.target.value)}
                    placeholder="Nombre del item"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={item.real}
                    onChange={(e) => handlePresupuestoChange(item.id, e.target.value)}
                    placeholder="USD 0"
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="remove-presupuesto-btn"
                    onClick={() => removePresupuestoItem(item.id)}
                    title="Eliminar item"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="tareas-section">
        <div className="section-header">
          <div className="section-title-with-icon">
            <span className="section-icon floating-delayed">✅</span>
            <h2 className="section-title-text">Checklist de Tareas Pendientes</h2>
          </div>
          <button 
            type="button" 
            className="add-item-btn pulse-on-hover"
            onClick={addTarea}
          >
            <span className="btn-icon">➕</span>
            <span>Agregar Tarea</span>
          </button>
        </div>
        {data.tareas.length > 0 ? (
          <div className="tareas-grid">
            {data.tareas.map((tarea) => (
              <div key={tarea.id} className="tarea-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={tarea.completada || false}
                    onChange={() => handleTareaChange(tarea.id)}
                  />
                  <input
                    type="text"
                    className="tarea-nombre-input"
                    value={tarea.nombre}
                    onChange={(e) => updateTareaNombre(tarea.id, e.target.value)}
                    placeholder="Nombre de la tarea"
                    onClick={(e) => e.stopPropagation()}
                  />
                </label>
                <button
                  type="button"
                  className="remove-tarea-btn"
                  onClick={() => removeTarea(tarea.id)}
                  title="Eliminar tarea"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No hay tareas agregadas.</p>
            <p className="empty-state-hint">Haz clic en "Agregar Tarea" para comenzar.</p>
          </div>
        )}
      </section>

     

    </div>
  );
};

export default MonthReflection;

